import { useCallback } from "react";
import { useFormStore } from "../store/formStore";
import type { IntakeFormData, Mismatch } from "../types";
import type { useStepValidation } from "./useStepValidation";

/** Fields that show inline prefill / differs-from-proposal hints on step pages. */
export const INLINE_DIFF_FIELDS: ReadonlySet<string> = new Set([
  "companyLegalName",
  "sicCode",
  "zipCode",
  "city",
  "state",
  "effectiveDate",
  "deductibleYearType",
  "offeringDental",
  "offeringVision",
]);

/**
 * Convenience hook for wiring a form field to the store + validation.
 *
 * Returns everything a field needs: value, handlers, error, prefilled status, mismatch.
 * Mismatch detection is handled by the store's setField — every write triggers it
 * uniformly (typing, masked input, zip-lookup side effects, etc.).
 *
 * isPrefilled / mismatch are gated by INLINE_DIFF_FIELDS; the store retains the
 * full maps for the Review page to read directly.
 *
 * Usage:
 *   const name = useFormField("companyLegalName", validation);
 *   <input value={name.value} onChange={name.onChange} onBlur={name.onBlur} />
 *   {name.error && <p className="text-danger">{name.error}</p>}
 *   {name.isPrefilled && !name.mismatch && <p className="text-brand-green">Pre-filled from proposal</p>}
 *   {name.mismatch && <p className="text-warning">Changed from: {name.mismatch.original}</p>}
 */
export function useFormField<K extends keyof IntakeFormData>(
  field: K,
  validation?: ReturnType<typeof useStepValidation>,
) {
  const key = field as string;
  const value = useFormStore((s) => s.data[field]);
  const setField = useFormStore((s) => s.setField);
  const inlineEligible = INLINE_DIFF_FIELDS.has(key);
  const prefilled = useFormStore((s) => (inlineEligible ? s.prefilled[key] : false));
  const mismatch = useFormStore((s) =>
    inlineEligible ? (s.mismatches[key] as Mismatch | undefined) : undefined,
  );
  const error = validation?.getFieldError(key) ?? null;

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      setField(field, e.target.value as IntakeFormData[K]);
    },
    [field, setField],
  );

  const onBlur = useCallback(() => {
    validation?.touchField(key);
  }, [key, validation]);

  const setValue = useCallback(
    (val: IntakeFormData[K]) => {
      setField(field, val);
    },
    [field, setField],
  );

  return {
    value,
    onChange,
    onBlur,
    setValue,
    error,
    isPrefilled: !!prefilled,
    mismatch,
  };
}
